import React, { useState, useRef, useEffect, useMemo } from "react";
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Card,
    FormGroup,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Autocomplete } from "@react-google-maps/api";
import {
    Map,
    AdvancedMarker,
    useMap,
    useMapsLibrary
} from "@vis.gl/react-google-maps";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CrudService from 'services/cruds-service'
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import RoomIcon from '@mui/icons-material/Room';
import debounce from "lodash.debounce";
import GeofenceEntityAddressItemSelect from "../select-entity";
import { TerraDrawOverlay } from "components/TerraDrawOverlay";

const containerStyle = { width: "100%", height: "500px" };
const center = { lat: 42.454272, lng: -88.249899 }; // default center

const CircleOverlay = ({ center, radius, options, onChanges }) => {
    const map = useMap();
    const circleRef = useRef(null);

    const circleOptions = useMemo(() => ({
        ...options,
        center,
        radius
    }), [center, radius, options]);

    const debouncedNotifyChange = useMemo(() => debounce((eventName) => {
        if (circleRef.current && onChanges) {
            const circle = circleRef.current;
            onChanges('Circle', circle);
        }
    }, 200), [onChanges]);

    useEffect(() => {
        if (!map) return;

        const circle = new google.maps.Circle(circleOptions);
        circle.setMap(map);
        circleRef.current = circle;

        const eventNames = ["center_changed", "radius_changed", "dragstart", "drag", "dragend"];
        const listeners = eventNames.map(eventName =>
            google.maps.event.addListener(circle, eventName, () => debouncedNotifyChange(eventName))
        );

        return () => {
            circle.setMap(null);
            listeners.forEach(listener => google.maps.event.removeListener(listener));
            debouncedNotifyChange.cancel();
        };
    }, [map, circleOptions, debouncedNotifyChange]);

    return null;
};

const RectangleOverlay = ({ bounds, options, onChanges }) => {
    const map = useMap();
    const rectangleRef = useRef(null);

    const rectangleOptions = useMemo(() => ({
        ...options,
        bounds
    }), [options, bounds]);

    const debouncedNotifyChange = useMemo(() => debounce((eventName) => {
        if (rectangleRef.current && onChanges) {
            const currentBounds = rectangleRef.current.getBounds()?.toJSON();
            if (currentBounds) {
                onChanges('Rectangle', rectangleRef.current);
            }
        }
    }, 200), [onChanges]);

    useEffect(() => {
        if (!map) return;

        const rectangle = new google.maps.Rectangle(rectangleOptions);
        rectangle.setMap(map);
        rectangleRef.current = rectangle;

        const eventNames = ["bounds_changed", "dragstart", "drag", "dragend"];
        const listeners = eventNames.map(eventName =>
            google.maps.event.addListener(rectangle, eventName, () => {
                debouncedNotifyChange(eventName);
            })
        );

        return () => {
            rectangle.setMap(null);
            listeners.forEach(listener => google.maps.event.removeListener(listener));
            debouncedNotifyChange.cancel();
        };
    }, [map, rectangleOptions, debouncedNotifyChange]);

    return null;
};

const PolygonOverlay = ({ paths, options, onChanges }) => {
    const map = useMap();
    const polygonRef = useRef(null);

    const polygonOptions = useMemo(() => ({
        ...options,
        paths
    }), [paths, options]);

    const debouncedNotifyChange = useMemo(() => debounce((eventName) => {
        if (polygonRef.current && onChanges) {
            const polygon = polygonRef.current;
            onChanges('Polygon', polygon);
        }
    }, 200), [onChanges]);

    useEffect(() => {
        if (!map) return;

        const polygon = new google.maps.Polygon(polygonOptions);
        polygon.setMap(map);
        polygonRef.current = polygon;

        const eventNames = ["insert_at", "remove_at", "set_at"];
        const pathListeners = eventNames.map(event =>
            google.maps.event.addListener(polygon.getPath(), event, () => debouncedNotifyChange(event))
        );

        const dragEvents = ["dragstart", "drag", "dragend"];
        const dragListeners = dragEvents.map(event =>
            google.maps.event.addListener(polygon, event, () => debouncedNotifyChange(event))
        );

        return () => {
            polygon.setMap(null);
            [...pathListeners, ...dragListeners].forEach(listener => google.maps.event.removeListener(listener));
            debouncedNotifyChange.cancel();
        };
    }, [map, polygonOptions, debouncedNotifyChange]);

    return null;
};

// Custom DrawingManager component (removed in favor of TerraDrawOverlay)

const GeofenceEdit = () => {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        name: "",
        purpose: null,
        entity: null,
        description: "",
        latitude: "",
        longitude: "",
        radius: "",
        geofenceType: "polygon",
        color: "#ff0000",
        coordinates: [],
        status_type_id: 1,
        address_item_ids: []
    });
    const [types, setTypes] = useState([])
    const navigate = useNavigate()

    const [geofence, setGeofence] = useState(null);
    const [entityList, setEntityList] = useState([]);
    const [entityAddressItemList, setEntityAddressItemList] = useState([])
    const [dupEntityAddressItemList, setDupEntityAddressItemList] = useState([])
    const [entityAddressItem, setEntityAddressItem] = useState([]);
    const [mapCenter, setMapCenter] = useState(center); // Initial center (San Francisco)
    const [markerPosition, setMarkerPosition] = useState(null);
    const autocompleteRef = useRef(null);
    const [showGeofence, setShowGeofence] = useState(false)
    const [drawingMode, setDrawingMode] = useState("polygon")
    const [geofences, setGeofences] = useState([]);
    const [loading, setLoading] = useState(false)
    const map = useMap()
    // Handle changes in latitude & longitude
    const handleCoordinateChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        // ✅ Update map center when latitude or longitude changes
        if (name === "latitude" || name === "longitude") {
            setMapCenter({
                lat: parseFloat(name === "latitude" ? value : formData.latitude) || 0,
                lng: parseFloat(name === "longitude" ? value : formData.longitude) || 0
            });
        }
    };

    const handleGeofenceComplete = (feature) => {
        const geometry = feature.geometry;
        const mode = feature.properties?.mode;

        let newGeofence;
        let lat;
        let lng;
        let radius;
        let coords = [];

        if (!geometry) {
            return;
        }

        // -------------------------
        // POLYGON
        // -------------------------
        if (mode === "polygon") {
            const path = geometry.coordinates[0].map(([lng, lat]) => ({
                lat,
                lng,
            }));

            lat = path[0]?.lat;
            lng = path[0]?.lng;

            newGeofence = {
                type: "polygon",
                path,
            };

            coords = path;

            setFormData((prev) => ({
                ...prev,
                geofenceType: "polygon",
            }));
        }

        // -------------------------
        // RECTANGLE
        // -------------------------
        else if (mode === "rectangle") {
            const path = geometry.coordinates[0].map(([lng, lat]) => ({
                lat,
                lng,
            }));

            const lats = path.map((p) => p.lat);
            const lngs = path.map((p) => p.lng);

            const north = Math.max(...lats);
            const south = Math.min(...lats);
            const east = Math.max(...lngs);
            const west = Math.min(...lngs);

            lat = (north + south) / 2;
            lng = (east + west) / 2;

            newGeofence = {
                type: "rectangle",
                bounds: {
                    north,
                    south,
                    east,
                    west,
                },
            };

            coords = [
                { lat: north, lng: east },
                { lat: south, lng: west },
            ];

            setFormData((prev) => ({
                ...prev,
                geofenceType: "rectangle",
            }));
        }

        // -------------------------
        // CIRCLE
        // -------------------------
        else if (mode === "circle") {
            const coordinates = geometry.coordinates[0];

            const lats = coordinates.map(([lng, lat]) => lat);
            const lngs = coordinates.map(([lng, lat]) => lng);

            const north = Math.max(...lats);
            const south = Math.min(...lats);
            const east = Math.max(...lngs);
            const west = Math.min(...lngs);

            lat = (north + south) / 2;
            lng = (east + west) / 2;

            radius = feature.properties?.radiusKilometers
                ? feature.properties.radiusKilometers * 1000
                : 100;

            newGeofence = {
                type: "circle",
                center: {
                    lat,
                    lng,
                },
                radius,
            };

            coords = [
                {
                    lat,
                    lng,
                },
            ];

            setFormData((prev) => ({
                ...prev,
                geofenceType: "circle",
                radius,
            }));
        }

        else {
            console.warn("Unsupported Terra Draw mode:", mode);
            return;
        }
        setDrawingMode('static')
        setGeofence(newGeofence);

        setFormData((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            coordinates: coords,
        }));
        setTimeout(() => {
            setDrawingMode(mode);
        }, 100);
    };

    const handleGeofence = (shape, value) => {
        let newGeofence, lat, lng, radius, coords = [];

        if (shape === 'Polygon') {
            const path = value.getPath().getArray().map((latLng) => ({
                lat: latLng.lat(),
                lng: latLng.lng(),
            }));
            lat = path[0]?.lat;
            lng = path[0]?.lng;
            newGeofence = { type: "polygon", path };
            coords = path;
            setFormData((prev) => ({ ...prev, geofenceType: "polygon" }));
        }

        if (shape === 'Rectangle') {
            const bounds = value.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            lat = (ne.lat() + sw.lat()) / 2;
            lng = (ne.lng() + sw.lng()) / 2;

            // ✅ Fix: Rectangle bounds should be in {north, south, east, west}
            newGeofence = {
                type: "rectangle",
                bounds: {
                    north: ne.lat(),
                    south: sw.lat(),
                    east: ne.lng(),
                    west: sw.lng(),
                },
            };

            coords = [{ lat: ne.lat(), lng: ne.lng() }, { lat: sw.lat(), lng: sw.lng() }];
            setFormData((prev) => ({ ...prev, geofenceType: "rectangle" }));
        }

        if (shape === 'Circle') {
            const center = value.getCenter();
            radius = value.getRadius();
            lat = center.lat();
            lng = center.lng();
            newGeofence = { type: "circle", center, radius };
            coords = [{ lat, lng }];
            setFormData((prev) => ({ ...prev, geofenceType: "circle", radius }));
        }

        setGeofence(newGeofence);
        setFormData((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            coordinates: coords,
        }));

    }

    useEffect(() => {
        ; (async () => {
            const response = await CrudService.getGeofenceTypes()
            setTypes(response.data)
        })()
            // ; (async () => {
            //     const response = await CrudService.getEntitiesList()
            //     setEntityList(response.data)
            //     const transformedResponse = response.entity_address_item.map(item => ({
            //         ...item,
            //         id: item.address_item_id // Assuming address_item_id is the correct field name in your response
            //     }));
            //     setEntityAddressItemList(transformedResponse)
            // })()
            ; (async () => {
                const response = await CrudService.getAllGeofences()
                let geo = response.data.map(({ name, geofence_id, geofence_type, latitude, longitude, coordinates, radius, color }) => ({ name, geofence_id, geofence_type, latitude, longitude, coordinates, radius, color }))
                setGeofences(geo)
            })()
    }, [])

    useEffect(() => {
        if (!id) return
        (async () => {
            try {
                setLoading(true)
                const response = await CrudService.getEntitiesList()
                setEntityList(response.data)
                let filterEntityAddressItem = response?.entity_address_item?.filter((e) => e?.address_item_id != null) ?? []
                const transformedResponse = filterEntityAddressItem.map(item => ({
                    ...item,
                    id: item?.address_item_id // Assuming address_item_id is the correct field name in your response
                }));
                setEntityAddressItemList(transformedResponse)
                setDupEntityAddressItemList(transformedResponse)
                const resp = await CrudService.getSingleGeofence(id)

                if (resp.success) {
                    let data = resp.data;
                    let newGeofence;
                    setDrawingMode(data.geofence_type)
                    if (data.geofence_type === "polygon") {
                        newGeofence = {
                            type: "polygon",
                            path: data.coordinates.map(coord => ({
                                lat: parseFloat(coord.lat),
                                lng: parseFloat(coord.lng),
                            })),
                        };
                    } else if (data.geofence_type === "rectangle") {
                        newGeofence = {
                            type: "rectangle",
                            bounds: {
                                north: parseFloat(data.coordinates[0].lat),
                                east: parseFloat(data.coordinates[0].lng),
                                south: parseFloat(data.coordinates[1].lat),
                                west: parseFloat(data.coordinates[1].lng),
                            },
                        };
                    } else if (data.geofence_type === "circle") {
                        newGeofence = {
                            type: "circle",
                            center: { lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) },
                            radius: data.radius,
                        };
                    }
                    let addressItemList = []
                    if (data?.EntityItemGeofenceAssociations) {
                        addressItemList = data?.EntityItemGeofenceAssociations.map((e) => e.EntityAddressItem.address_item_id)
                        let filterList = transformedResponse.filter((e) => addressItemList.includes(e.id))
                        setEntityAddressItem(filterList)
                    }

                    setFormData({
                        name: data.name,
                        purpose: data.geofence_type_id,
                        entity: data.entity_id,
                        description: data.description,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        radius: data.radius,
                        geofenceType: data.geofence_type,
                        color: data.color,
                        coordinates: data.coordinates,
                        status_type_id: data.status_type_id,
                        priority: 'low',
                        address_item_ids: addressItemList
                    });
                    if (data?.entity_id) {
                        let values = JSON.parse(JSON.stringify(transformedResponse.filter((e) => e.entity_id == data?.entity_id)))
                        setDupEntityAddressItemList(values)
                    }
                    setMapCenter({
                        lat: parseFloat(data.latitude) || 0,
                        lng: parseFloat(data.longitude) || 0
                    });
                    setMarkerPosition({
                        lat: parseFloat(data.latitude) || 0,
                        lng: parseFloat(data.longitude) || 0
                    });
                    setGeofence(newGeofence);
                } else {
                    toast.error(resp.error)
                }
                setLoading(false)
            } catch (error) {
                console.error('single geofence', error)
                toast.error(error.error)
                setLoading(false)
            }
        })()
    }, [id])

    useEffect(() => {
        if (map && markerPosition) {
            map.setCenter(markerPosition)
        }
    }, [map, markerPosition])

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (e.target.name === 'entity') {
            changeEntityAddressItem(e.target.value)
        }
        if (e.target.name === 'geofenceType') {
            setDrawingMode(e.target.value)
        }
    };

    const changeEntityAddressItem = (value) => {
        if (value) {
            let values = JSON.parse(JSON.stringify(entityAddressItemList.filter((e) => e.entity_id == value)))
            setDupEntityAddressItemList(values)
        }
    }

    const handleSave = async (e) => {
        e.preventDefault();

        // Basic Validation Rules
        if (!formData.name.trim()) {
            toast.info("Geofence Name is required.");
            return;
        }
        if (!formData.purpose) {
            toast.info("Please select a purpose.");
            return;
        }
        if (!formData.entity) {
            toast.info("Please select a entity.");
            return;
        }
        if (!formData.description.trim()) {
            toast.info("Description is required.");
            return;
        }
        if (!formData.latitude || !formData.longitude) {
            toast.info("Please draw a geofence on the map to get coordinates.");
            return;
        }
        if (formData.geofenceType === "circle" && (!formData.radius || formData.radius <= 0)) {
            toast.info("Please provide a valid radius for the circle geofence.");
            return;
        }
        if (!formData.color) {
            toast.info("Please select a color.");
            return;
        }

        if (entityAddressItem.length > 0) {
            formData.address_item_ids = entityAddressItem.map((e) => e.address_item_id);
        } else {
            formData.address_item_ids = [];
        }

        try {
            let resp = await CrudService.updateGeofence(formData, id)

            if (resp.success) {
                toast.success(resp.msg)
                navigate('/geofence')
            }
        } catch (error) {
            console.error('failed api', error)
            toast.error(error.error)
        }
    };

    const onPlaceSelected = () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
            const { lat, lng } = place.geometry.location;
            setFormData({ ...formData, latitude: lat(), longitude: lng() });
            setMapCenter({ lat: lat(), lng: lng() });
            // map.setCenter({ lat: lat(), lng: lng() })
            setMarkerPosition({ lat: lat(), lng: lng() });
        }
    };

    // Drawing options are now handled by TerraDrawOverlay via its props

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox mt={5} mb={9}>
                <Grid container justifyContent="center">
                    <Grid item xs={12} lg={8}>
                        <MDBox mt={6} mb={8} textAlign="center">
                            <MDBox mb={1}>
                                <MDTypography variant="h3" fontWeight="bold">
                                    Add New Geofence
                                </MDTypography>
                            </MDBox>
                            <MDTypography variant="h5" fontWeight="regular" color="secondary">
                                This information will describe more about the Geofence.
                            </MDTypography>
                        </MDBox>
                        <Card>
                            <MDBox component="form" method="POST" onSubmit={(e) => handleSave(e)}>
                                <MDBox display="flex" flexDirection="column" px={3} my={2}>
                                    <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} margin="normal" />

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Entity</InputLabel>
                                        <Select label="Entity" name="entity" value={formData.entity ?? ''} onChange={handleChange} sx={{ height: '40px' }} readOnly={formData.address_item_id}>
                                            {
                                                entityList.length && entityList.map((elm) => (
                                                    <MenuItem key={elm.entity_id} value={elm.entity_id}>{elm.name || elm.first_name}</MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Purpose</InputLabel>
                                        <Select label="Purpose" name="purpose" value={formData.purpose ?? ''} onChange={handleChange} sx={{ height: '40px' }}>
                                            {
                                                types.length && types.map((elm) => (
                                                    <MenuItem key={elm.geofence_type_id} value={elm.geofence_type_id}>{elm.type_name}</MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>

                                    <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} margin="normal" multiline rows={3} />

                                    <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={onPlaceSelected}>
                                        <TextField fullWidth label="Search Address" margin="normal" />
                                    </Autocomplete>

                                    <TextField fullWidth label="Latitude" name="latitude" value={formData.latitude} margin="normal" onChange={handleCoordinateChange} />
                                    <TextField fullWidth label="Longitude" name="longitude" value={formData.longitude} margin="normal" onChange={handleCoordinateChange} />

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Geofence Type</InputLabel>
                                        <Select label="Geofence Type" name="geofenceType" value={formData.geofenceType} onChange={handleChange} sx={{ height: '40px' }} >
                                            <MenuItem value="polygon">Polygon</MenuItem>
                                            <MenuItem value="rectangle">Rectangle</MenuItem>
                                            <MenuItem value="circle">Circle</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {formData.geofenceType === "circle" && (
                                        <TextField fullWidth label="Radius (meters)" name="radius" type="number" value={formData.radius} onChange={handleChange} margin="normal" />
                                    )}

                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select label="Status" name="status_type_id" value={formData.status_type_id} onChange={handleChange} sx={{ height: '40px' }} >
                                            <MenuItem value="1">Active</MenuItem>
                                            <MenuItem value="2">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField fullWidth label="Color" name="color" type="color" value={formData.color} onChange={handleChange} margin="normal" />
                                    {!loading && <GeofenceEntityAddressItemSelect entityAddressItems={dupEntityAddressItemList} entityAddressItem={entityAddressItem} setEntityAddressItem={setEntityAddressItem} />}
                                    <FormGroup>
                                        <FormControlLabel control={<Switch checked={showGeofence} onChange={() => setShowGeofence(!showGeofence)} />} label="Show Geofences" />
                                    </FormGroup>
                                    <MDBox display="flex" gap={1} mb={2} flexWrap="wrap">
                                        <MDButton
                                            variant="gradient"
                                            color={drawingMode === "static" ? "info" : "dark"}
                                            size="small"
                                            onClick={() => setDrawingMode("static")}
                                        >
                                            Select / Clear
                                        </MDButton>
                                    </MDBox>
                                    <Map
                                        style={containerStyle}
                                        defaultCenter={mapCenter}
                                        defaultZoom={14}
                                        mapId="d089c1a7f8831a36" // Add your Google Maps Map ID here
                                    >
                                        <TerraDrawOverlay
                                            color={formData.color}
                                            drawingMode={drawingMode}
                                            onShapeComplete={handleGeofenceComplete}
                                        />

                                        {showGeofence && geofences.map((geofence, index) => {
                                            const position = {
                                                lat: parseFloat(geofence.latitude),
                                                lng: parseFloat(geofence.longitude),
                                            };

                                            return (
                                                <React.Fragment key={index}>
                                                    <AdvancedMarker position={position}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <h6 style={{ margin: 0 }} >{geofence.name}</h6>
                                                            <RoomIcon sx={{ color: 'red', fontSize: '30px !important' }} />
                                                        </div>
                                                    </AdvancedMarker>

                                                    {geofence.geofence_type === "circle" && (
                                                        <CircleOverlay
                                                            center={position}
                                                            radius={geofence.radius}
                                                            options={{
                                                                fillColor: geofence.color,
                                                                fillOpacity: 0.3,
                                                                strokeColor: geofence.color,
                                                                strokeWeight: 2,
                                                            }}
                                                        />
                                                    )}

                                                    {geofence.geofence_type === "rectangle" && geofence.coordinates.length === 2 && (
                                                        <RectangleOverlay
                                                            bounds={{
                                                                north: Math.max(geofence.coordinates[0].lat, geofence.coordinates[1].lat),
                                                                south: Math.min(geofence.coordinates[0].lat, geofence.coordinates[1].lat),
                                                                east: Math.max(geofence.coordinates[0].lng, geofence.coordinates[1].lng),
                                                                west: Math.min(geofence.coordinates[0].lng, geofence.coordinates[1].lng),
                                                            }}
                                                            options={{
                                                                fillColor: geofence.color,
                                                                fillOpacity: 0.3,
                                                                strokeColor: geofence.color,
                                                                strokeWeight: 2,
                                                            }}
                                                        />
                                                    )}

                                                    {geofence.geofence_type === "polygon" && (
                                                        <PolygonOverlay
                                                            paths={geofence.coordinates}
                                                            options={{
                                                                fillColor: geofence.color,
                                                                fillOpacity: 0.3,
                                                                strokeColor: geofence.color,
                                                                strokeWeight: 2,
                                                            }}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}

                                        {geofence && geofence.type === "polygon" && (
                                            <PolygonOverlay
                                                paths={geofence.path}
                                                options={{ fillColor: formData.color, fillOpacity: 0.3, strokeWeight: 2, editable: true, draggable: true }}
                                                onChanges={(shape, value) => handleGeofence(shape, value)}
                                            />
                                        )}
                                        {geofence && geofence.type === "rectangle" && (
                                            <RectangleOverlay
                                                bounds={geofence.bounds}
                                                options={{ fillColor: formData.color, fillOpacity: 0.3, strokeWeight: 2, editable: true, draggable: true }}
                                                onChanges={(shape, value) => handleGeofence(shape, value)}
                                            />
                                        )}
                                        {geofence && geofence.type === "circle" && (
                                            <CircleOverlay
                                                center={geofence.center}
                                                radius={geofence.radius}
                                                options={{ fillColor: formData.color, fillOpacity: 0.3, strokeWeight: 2, editable: true, draggable: true }}
                                                onChanges={(shape, value) => handleGeofence(shape, value)}
                                            />
                                        )}
                                        {markerPosition && <AdvancedMarker position={markerPosition} />}
                                    </Map>
                                    <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">
                                        <MDBox mx={2}>
                                            <MDButton
                                                variant="gradient"
                                                color="dark"
                                                size="small"
                                                px={2}
                                                mx={2}
                                                onClick={() =>
                                                    navigate("/geofence", {
                                                        state: { value: false, text: "" },
                                                    })
                                                }
                                            >
                                                Back
                                            </MDButton>
                                        </MDBox>
                                        <MDButton variant="gradient" color="dark" size="small" type="submit">
                                            Update
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default GeofenceEdit;
